import { create } from 'zustand'
import { combine } from 'zustand/middleware'

import type { Origin, User, UserInformation } from './user/types'
import type { AuthState, AuthStateType } from './types'


import { emitAuthUpdate } from './events'

export interface AuthStoreState {
  state: AuthState
}

function initialState(): AuthStoreState {
  const info = getLocalStorage()
  if (info === null) {
    return {
      state: { type: 'LoggedOut' },
    }
  }

  return {
    state: { type: 'LoggedOut', data: { token: info.token } },
  }
}

export const useAuthStore = create(
  combine(initialState(), (set, get) => {
    return {
      update({ user, token }: UserInformation) {
        emitAuthUpdate({ type: 'UserUpdated', data: { user, token } })
        updateLocalStorage({ user, token })

        // update the real user state
        return set({
          state: { type: 'LoggedIn', data: { user, token } },
        })
      },

      logout() {
        set({
          state: { type: 'LoggingOut' },
        })
        emitAuthUpdate({ type: 'LogoutRequest' })
        emitAuthUpdate({ type: 'LoggingOut' })

        updateLocalStorage(null)
        set({
          state: { type: 'LoggedOut' },
        })
        emitAuthUpdate({ type: 'LoggedOut' })
      },

      onLoginRequest() {
        const { state } = get()

        const user = state?.data && 'user' in state.data
          ? state.data.user
          : undefined

        emitAuthUpdate({ type: 'LoginRequest', user: user})
        return set({
          state: { type: 'LoggingIn', data: state.data as any },
        })
      },

      onLoginSuccess({ user, token, origin }) {
        const { state } = get()

        if (isAuthState(state, ['LoggedIn'])) {
          // NOTE: Yes we have to allow this, because we probably are already logged in by anonymous user
          // not allowed if the user is already logged in
          // throw new AuthError(state.type, ['LoggedIn'], 'Already logged in')
        }

        emitAuthUpdate({ type: 'LoginSuccess', data: { user, token, origin } })
        updateLocalStorage({ user, token })

        // otherwise, keep the anonymous state the same and update the user state
        return set({
          state: { type: 'LoggedIn', data: { user, token, origin } },
        })
      },

      onLoginFailure(error: string | Error) {
        const { state } = get()

        if (isAuthState(state, ['LoggedIn'])) {
          // not allowed if already logged in
          throw new AuthError(state.type, ['LoggedIn'], 'Already logged in')
        }

        const user = state?.data && 'user' in state.data
          ? state.data.user
          : undefined

        emitAuthUpdate({ type: 'LoginFailure', error })

        return set({
          state: { type: 'LoggingInError', data: { error, user } },
        })
      },

      updateOrigin(origin: Origin) {
        const { state } = get()
        
        if (!isAuthState(state, ['LoggedIn'])) {
          return // Only update origin if logged in
        }
        
        return set({
          state: { 
            ...state,
            data: { ...state.data, origin }
          }
        })
      },
    }
  })
)

type AuthStoreSubscribe = (state: AuthStoreState) => void
export const authStoreSubscribe = (handler: AuthStoreSubscribe) => {
  return useAuthStore.subscribe(handler)
}

export class AuthError extends Error {
  constructor(
    public readonly value: AuthStateType,
    public readonly states: readonly AuthStateType[] | AuthStateType[] = [],
    message = `Expected auth state to be in [${states}], got ${value}`
  ) {
    super(message)
  }
}

/**
 * @returns {boolean} whether the state in one of the specified states
 */
export function isAuthState<State extends AuthState, Targets extends readonly AuthStateType[]>(
  state: State,
  states: Targets
): state is Extract<State, { type: Targets[number] }> {
  return states.includes(state.type)
}

function getLocalStorage(): null | UserInformation {
  const token = localStorage.getItem('token')
  const userJson = localStorage.getItem('user')

  if (!userJson || !token) return null

  try {
    const user = JSON.parse(userJson) as User
    return { user, token }
  } catch (err) {
    console.error(err)
    return null
  }
}

function updateLocalStorage(info: null | UserInformation) {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('anonymous_hash')

  if (!info) return
  localStorage.setItem('token', info.token)
  localStorage.setItem('user', JSON.stringify(info.user))
  if (info.user.anonymousHash) {
    localStorage.setItem('anonymous_hash', info.user.anonymousHash)
  }
}
